import React, { useMemo } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import StatusChip from "./StatusChip";
import { formatDateTime } from "utils/formatters";

const valueOf = (source, key) => source?.[key] ?? source?.[`${key[0].toUpperCase()}${key.slice(1)}`];

const dash = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

// const formatTimelineDate = (value) => {
//   if (!value) return "-";
//   const date = new Date(value);
//   if (Number.isNaN(date.getTime())) return "-";
//   return new Intl.DateTimeFormat("vi-VN", {
//     day: "2-digit",
//     month: "2-digit",
//     year: "numeric",
//     hour: "2-digit",
//     minute: "2-digit",
//   }).format(date);
// };

const normalizeStatusKey = (value) =>
  String(value || "")
    .trim()
    .toLocaleLowerCase("vi-VN")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d");

const isApprovedStatus = (status) => {
  const value = normalizeStatusKey(status);
  return value.includes("da phe duyet") || value.includes("duoc phe duyet");
};

const isActiveStepStatus = (status) => {
  const value = normalizeStatusKey(status);
  return value.includes("dang xu ly");
};
const userNameOf = (step) =>
  step?.fullName ||
  step?.FullName ||
  step?.userName ||
  step?.UserName ||
  step?.approverName ||
  step?.ApproverName ||
  "-";

const latestApprovalProfile = (approvals = []) =>
  [...approvals].sort((left, right) => {
    const submitCountDiff = Number(valueOf(right, "submitCount") || 0) - Number(valueOf(left, "submitCount") || 0);
    if (submitCountDiff) return submitCountDiff;

    const rightDate = new Date(valueOf(right, "submittedAt") || valueOf(right, "createdAt") || 0).getTime();
    const leftDate = new Date(valueOf(left, "submittedAt") || valueOf(left, "createdAt") || 0).getTime();
    return rightDate - leftDate;
  })[0] || null;

const stepByOrder = (profile, order) =>
  (valueOf(profile, "steps") || []).find((step) => Number(valueOf(step, "stepOrder")) === order);

const APPROVAL_STEP_LABELS = ["Trợ lý KHTH", "Trưởng phòng KHTH", "Trưởng phòng Tài chính", "Ban giám đốc"];

const timelineFromData = (subcontract, approvals, labels = {}) => {
  const profile = latestApprovalProfile(approvals);
  const steps = [1, 2, 3, 4].map((order) => stepByOrder(profile, order));
  const profileStatus = valueOf(profile, "status") || valueOf(subcontract, "approvalStatus");

  const timeline = [
    {
      key: "created",
      title: "Bộ phận sản xuất",
      subtitle: "Khởi tạo giao khoán",
      person: valueOf(subcontract, "createBy") || valueOf(subcontract, "createdBy"),
      date: valueOf(subcontract, "createdAt"),
      // status: valueOf(approvals, "status") || "Tạo mới",
      status: profileStatus,
      state: "done",
    },
    ...steps.map((step, index) => {
      const status = valueOf(step, "status");
      return {
        key: `step-${index + 1}`,
        title: `${index + 1}. ${APPROVAL_STEP_LABELS[index]}`,
        subtitle: `${valueOf(step, "stepName") || APPROVAL_STEP_LABELS[index]} phê duyệt`,
        person: userNameOf(step),
        date: valueOf(step, "actedAt"),
        status,
        state: isApprovedStatus(status) ? "done" : isActiveStepStatus(status) ? "active" : "upcoming",
      };
    }),
    {
      key: "completed",
      title: "Hoàn tất",
      subtitle: "Hoàn tất phê duyệt",
      person: userNameOf(steps[3]),
      date: valueOf(profile, "respondedAt"),
      status: profileStatus,
      state: isApprovedStatus(profileStatus) && (valueOf(profile, "respondedAt") || valueOf(profile, "finalApprovedAt")) ? "done" : "upcoming",
    },
  ];

  if (labels.originTitle) timeline[0].title = labels.originTitle;
  if (labels.originSubtitle) timeline[0].subtitle = labels.originSubtitle;
  if (labels.stepActionLabel) {
    timeline.slice(1, -1).forEach((item, index) => {
      item.subtitle = `${valueOf(steps[index], "stepName") || APPROVAL_STEP_LABELS[index]} ${labels.stepActionLabel}`;
    });
  }
  if (labels.completedTitle) timeline[timeline.length - 1].title = labels.completedTitle;
  if (labels.completedSubtitle) timeline[timeline.length - 1].subtitle = labels.completedSubtitle;

  return timeline;
};

export default function ApprovalTimeline({ subcontract = {}, approvals = [], labels = {} }) {
  const theme = useTheme();
  const items = useMemo(() => timelineFromData(subcontract, approvals, labels), [subcontract, approvals, labels]);

  const activeIndex = Math.max(
    0,
    items.findIndex((item) => item.state !== "done"),
  );
  const resolvedActiveIndex = activeIndex === -1 ? items.length - 1 : activeIndex;

  const circleStyle = (state, index) => {
    if (state === "done") {
      return {
        bgcolor: theme.palette.success.light,
        color: theme.palette.success.contrastText,
        borderColor: theme.palette.success.light,
      };
    }

    if (index === resolvedActiveIndex || state === "active") {
      return {
        bgcolor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderColor: theme.palette.primary.main,
      };
    }

    return {
      bgcolor: theme.palette.background.paper,
      color: theme.palette.text.secondary,
      borderColor: theme.palette.divider,
    };
  };

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        mb: 2,
        borderRadius: "10px",
        bgcolor: theme.palette.background.paper,
        overflowX: "auto",
        px: "12px",
        py: "24px",
      }}
    >
      <Box
        sx={{
          minWidth: 780,
          display: "grid",
          gridTemplateColumns: "repeat(6, minmax(130px, 1fr))",
          gap: 1.5,
          position: "relative",
          justifyItems: "center",
        }}
      >
        {items.map((item, index) => {
          const circle = circleStyle(item.state, index);
          return (
            <Box key={item.key} sx={{ position: "relative", zIndex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: 0.75 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    border: `1px solid ${circle.borderColor}`,
                    bgcolor: circle.bgcolor,
                    color: circle.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 800,
                    flex: "0 0 auto",
                  }}
                >
                  {index + 1}
                </Box>
                <Typography fontWeight={750} fontSize={13} noWrap title={item.title}>
                  {item.title}
                </Typography>
              </Box>
              <Box sx={{ pl: "36px" }}>
                <Typography fontSize={12} color="text.secondary" noWrap title={dash(item.subtitle)}>
                  {dash(item.subtitle)}
                </Typography>
                <Typography fontSize={12} sx={{ mt: 0.5 }} noWrap title={dash(item.person)}>
                  {dash(item.person)}
                </Typography>
                <Typography fontSize={12} color="text.secondary" sx={{ mt: 0.5 }} noWrap title={formatDateTime(item.date)}>
                  {formatDateTime(item.date)}
                </Typography>
                <Box sx={{ mt: 0.75 }}>
                  {item.status ? <StatusChip value={item.status} /> : <Typography fontSize={12}>-</Typography>}
                </Box>
              </Box>

            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
